import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PrimeReactProvider } from 'primereact/api';
import 'primereact/resources/themes/lara-light-cyan/theme.css';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { IdleTimerProvider } from 'react-idle-timer';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import loginApi from './api/services/login.service';
import CookieConsentModal from './components/AcceptAllCookies/Cookies';
import Notifications from './components/Generics/Notifications';
import LoaderComponent from './components/LoaderComponent';
import { ROUTES } from './constants';
import { ToastContextProvider } from './contexts/ToastContext';
import { useAuth } from './hooks/useAuth';
import { useTabSync } from './hooks/useTabSync';
import {
  closeNotification,
  setIsUnread,
  // openNotification,
} from './redux/slice/Notification/notifications';
import { clearUser } from './redux/slice/Scheduling/getServices';
import { getUserProfileDataCall } from './redux/slice/UserProfileData/userProfileDataSlice';
import {
  clearingPermissionData,
  getUserPermissionCall,
  savingLoggedUserDetails,
  savingOrgId,
  savingOrgName,
  savingUserId,
  setFirstTimeUserValue,
} from './redux/slice/getUserPermission/getUserPermissionSlice';
import RoutesComponent from './routes';
import { useLocation, useNavigationType } from 'react-router-dom';
import CoSign from './api/services/UserDashboard/getNotificationforCoSign.service';

function App(): React.JSX.Element {
  const [, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const { login } = useAuth();
  useTabSync({
    onTokenReceived: (rawToken: string) => {
      const token = JSON.parse(rawToken);
      if (token?.userId && token?.orgId) {
        setLoading(true);
        dispatch(getUserProfileDataCall());
        dispatch(getUserPermissionCall()).then(() => {
          setLoading(false);
        });
        dispatch(savingUserId(token.userId));
        dispatch(savingOrgId(token.orgId));
        dispatch(savingOrgName(token.organizations));
        dispatch(savingLoggedUserDetails({ username: token.username }));
      }
    },
    onLogoutReceived: () => {
      logout().then(() => {
        dispatch(clearingPermissionData());
        dispatch(clearUser());
        navigate(ROUTES.LoginPage);
      });
    },
  });
  const url = window.location.href?.includes(
    // ROUTES.LoginPage ||
    ROUTES.OrganizationPage ||
      ROUTES.RegisterPage ||
      ROUTES.changePassword ||
      ROUTES.ForgotPassword ||
      ROUTES.sendEmail ||
      ROUTES.SignUp ||
      ROUTES.changePasswordWithLayout
  );
  const user = useSelector(({ getUserPermission }: any) => getUserPermission);
  const notifications = useSelector((state: any) => state.notifications);
  const userProfileData = useSelector((state: any) => state.userProfileData.value);
  const dispatch = useDispatch<any>();
  const [isLoggedOut, setIsLoggedOut] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType(); // 'POP' when browser back/forward
  const prevPathRef = React.useRef(location.pathname);
  const { logout } = useAuth();
  const handleOnIdle = async (): Promise<void> => {
    if (!url) {
      setIsLoggedOut(true);
      try {
        await loginApi.userLogout();
      } finally {
        logout().then(() => {
          dispatch(clearingPermissionData());
          dispatch(clearUser());
          setTimeout(() => {
            navigate(ROUTES.LoginPage);
          }, 4000);
          setCount(0);
        });
      }
    }
  };
  const handleOnActive = (): void => {
    if (isLoggedOut) {
      setIsLoggedOut(false);
    }
  };
  const access_token =
    sessionStorage?.access_token !== undefined ? JSON.parse(sessionStorage?.access_token) : '';
  const userExist = useSelector(({ getUserPermission }: any) => getUserPermission);
  const initializer = useRef(false);
  useEffect(() => {
    if (!initializer.current) {
      initializer.current = true;
      if (!access_token) {
        setLoading(false);
      } else if (access_token?.userId && access_token?.orgId && !userExist?.value?.data?.userId) {
        dispatch(getUserProfileDataCall());
        dispatch(getUserPermissionCall()).then(() => {
          setLoading(false);
        });
        dispatch(savingUserId(access_token?.userId));
        dispatch(savingOrgId(access_token?.orgId));
        dispatch(savingOrgName(access_token?.organizations));
        dispatch(
          savingLoggedUserDetails({
            username: access_token?.username,
          })
        );
      } else if (
        userExist?.value?.data?.userId &&
        userProfileData?.data?.hasOwnProperty('isResetPassword')
      ) {
        dispatch(setFirstTimeUserValue(userProfileData?.data?.isResetPassword));
        // dispatch(SavingLoginData(access_token));
        login({
          userId: userExist?.value?.data?.userId || access_token?.userId,
          orgId: userExist?.value?.data?.orgId || access_token?.orgId,
        }).then(() => {
          setLoading(false);
        });
      }
    }
  }, []);
  const [isCookieModalOpen, setIsCookieModalOpen] = useState(false);
  useEffect(() => {
    const consent = localStorage.getItem('cookieYesConsent');
    if (consent !== 'true') {
      setIsCookieModalOpen(true);
    }
  }, []);
  const closeModal = (): void => {
    setIsCookieModalOpen(false);
  };

  const handleCloseNotification = useCallback(() => {
    dispatch(closeNotification());
  }, [dispatch]);

  const handleCloseLogout = useCallback(() => {
    setIsLoggedOut(false);
  }, []);

  // Memoize notification props to prevent unnecessary re-renders
  const notificationProps = useMemo(
    () => ({
      title: notifications.title,
      open: notifications.isOpen,
      onClose: handleCloseNotification,
      success: notifications.success,
    }),
    [notifications.title, notifications.isOpen, notifications.success, handleCloseNotification]
  );
  React.useEffect(() => {
    const prevPath = prevPathRef.current;
    const currentPath = location.pathname;

    const CHANGE_PASSWORD_PATH = ROUTES.ChangePasswordPage ?? '/change-password';

    if (navigationType === 'POP' && prevPath === CHANGE_PASSWORD_PATH && currentPath !== prevPath) {
      sessionStorage.removeItem('access_token');
      navigate(ROUTES.LoginPage);
      sessionStorage.clear();
      dispatch(clearingPermissionData());
    }

    prevPathRef.current = currentPath;
  }, [location.pathname, navigationType, dispatch]);

  const handleNotification = async (type = 'unread'): Promise<any> => {
    if (!access_token?.userId || !access_token?.orgId) return;

    // Fall back to access_token.userId in case the user redux slice
    // (getUserPermission) hasn't finished loading yet on first mount.
    const userSpecificId = user?.value?.data?.userId || user?.userId || access_token?.userId;
    if (!userSpecificId) return;

    const isAdmin = user?.userRoles?.data?.roleName === 'Admin';
    const [res, auditingResponse, technicianResponse] = await Promise.all([
      CoSign.getNotification({
        userSpecificId,
        type: type,
      }),
      CoSign.getAudutingNotification({ type: type }),
      isAdmin ? CoSign.technicianNotification({ type: type }) : Promise.resolve(null),
    ]);

    // Red dot reflects the OR of all 3 notification sources so one source
    // returning empty can't clobber another source that has unread items.
    if (type === 'unread') {
      const cosignHasUnread =
        (res?.data?.data?.bulkNotifications?.length ?? 0) > 0 ||
        (res?.data?.data?.notifications?.length ?? 0) > 0;
      const auditingHasUnread = (auditingResponse?.data?.data?.length ?? 0) > 0;
      const technicianHasUnread = isAdmin && (technicianResponse?.data?.data?.length ?? 0) > 0;
      dispatch(setIsUnread(cosignHasUnread || auditingHasUnread || technicianHasUnread));
    }
  };

  useEffect(() => {
    handleNotification();
  }, [access_token?.userId]);

  return (
    <IdleTimerProvider
      timeout={3 * 60 * 60 * 1000 + 50 * 60 * 1000} // 3 hours 50 minutes
      onIdle={handleOnIdle}
      onActive={handleOnActive}
      debounce={500}
    >
      <DndProvider backend={HTML5Backend}>
        <ToastContextProvider>
          <PrimeReactProvider>
            <Notifications {...notificationProps} />
            {isLoggedOut && (
              <Notifications
                title={'Session Expired'}
                description={
                  'Session expired due to inactivity, Log in to continue where you left off!'
                }
                open={isLoggedOut}
                onClose={handleCloseLogout}
                success={notifications.success}
                isSession={true}
              />
            )}
            {loading ? <LoaderComponent /> : <RoutesComponent />}
            {isCookieModalOpen && (
              <CookieConsentModal isOpen={isCookieModalOpen} onClose={closeModal} />
            )}
          </PrimeReactProvider>
        </ToastContextProvider>
      </DndProvider>
    </IdleTimerProvider>
  );
}
export default App;
